const MIN_INTEREST_DAYS = 30;

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const endOfDay = (date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

const laterDate = (a, b) => (a > b ? a : b);
const earlierDate = (a, b) => (a < b ? a : b);

/**
 * Calendar day in local time.
 * YYYY-MM-DD and UTC-midnight values stay on that calendar date (no IST -1 day).
 */
const calendarStartOfDay = (value) => {
  if (value == null || value === '') return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [y, m, d] = trimmed.split('-').map(Number);
      return startOfDay(new Date(y, m - 1, d));
    }
    const utcMidnight = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T00:00:00(\.\d+)?Z$/i);
    if (utcMidnight) {
      return startOfDay(new Date(
        Number(utcMidnight[1]),
        Number(utcMidnight[2]) - 1,
        Number(utcMidnight[3])
      ));
    }
  }

  const d = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  if (
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0
  ) {
    return startOfDay(new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  }

  return startOfDay(d);
};

/** Inclusive calendar-day count (1 Jan–30 Jan = 30). */
const inclusiveDayCount = (from, to) => {
  const a = startOfDay(from);
  const b = startOfDay(to);
  if (b < a) return 0;
  return Math.round((b - a) / (1000 * 60 * 60 * 24)) + 1;
};

/** Skip the 31st so a 31-day month counts as 30 interest days. */
const countedInterestDays = (from, to) => {
  const a = startOfDay(from);
  const b = startOfDay(to);
  if (b < a) return 0;
  let count = 0;
  const cursor = new Date(a);
  while (cursor <= b) {
    if (cursor.getDate() !== 31) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const parseLocalDate = (value) => {
  const start = calendarStartOfDay(value);
  return start ? endOfDay(start) : null;
};

const getLoanStartDate = (article) => {
  if (article.loans?.length) {
    const sorted = [...article.loans].sort(
      (a, b) => calendarStartOfDay(a.date) - calendarStartOfDay(b.date)
    );
    return calendarStartOfDay(sorted[0].date) || startOfDay(new Date());
  }
  if (article.date) return calendarStartOfDay(article.date) || startOfDay(new Date());
  return startOfDay(new Date());
};

const toDateInputValue = (date) => {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const dailyFactor = (rate) => (Number(rate) / 100) / 12 / MIN_INTEREST_DAYS;

const advancePaidUptoByWholeMonths = (article, currentPaidUpto, monthsNum) => {
  const months = Number(monthsNum) || 0;
  if (months <= 0) return currentPaidUpto || null;

  const accrueFrom = currentPaidUpto
    ? startOfDay(addDays(currentPaidUpto, 1))
    : getLoanStartDate(article);

  return endOfDay(addDays(accrueFrom, months * MIN_INTEREST_DAYS - 1));
};

const rebuildInterestPaidUpto = (article) => {
  const interestPayments = (article.payments || [])
    .filter((p) => p.type === 'interest')
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  let upto = null;

  for (const p of interestPayments) {
    const monthsNum = Number(p.months) || 0;
    if (monthsNum > 0) {
      upto = advancePaidUptoByWholeMonths(article, upto, monthsNum);
    } else if (p.date) {
      const payDate = parseLocalDate(p.date);
      if (!Number.isNaN(payDate.getTime()) && (!upto || payDate > upto)) {
        upto = payDate;
      }
    }
  }

  if (!upto && article.interestPaidUpto) {
    const legacy = parseLocalDate(article.interestPaidUpto);
    if (!Number.isNaN(legacy.getTime())) upto = legacy;
  }

  return upto;
};

const listArticleLoans = (article) => {
  if (article.loans?.length) {
    return article.loans.map((loan) => ({
      date: calendarStartOfDay(loan.date) || startOfDay(loan.date),
      amount: Number(loan.amount) || 0,
      interestRate: loan.interestRate != null && loan.interestRate !== ''
        ? Number(loan.interestRate)
        : Number(article.interestRate)
    }));
  }
  if (article.amount) {
    return [{
      date: calendarStartOfDay(article.date) || startOfDay(article.date),
      amount: Number(article.amount) || 0,
      interestRate: Number(article.interestRate)
    }];
  }
  return [];
};

/** FIFO: repayments reduce oldest loans first, effective from the next day. */
const allocateRepaymentsFifo = (allLoans, repayments) => {
  const remaining = allLoans.map((loan) => loan.amount);
  const reductions = allLoans.map(() => []);

  const sorted = [...repayments].sort((a, b) => a.date - b.date);
  for (const repayment of sorted) {
    let left = Number(repayment.amount) || 0;
    const effectiveDate = startOfDay(addDays(repayment.date, 1));
    for (let i = 0; i < allLoans.length && left > 0; i++) {
      if (remaining[i] <= 0) continue;
      if (startOfDay(repayment.date) < allLoans[i].date) continue;
      const take = Math.min(remaining[i], left);
      remaining[i] -= take;
      left -= take;
      reductions[i].push({ effectiveDate, amount: take });
    }
  }

  return reductions;
};

const remainingOnDate = (originalAmount, reductions, day) => {
  let principal = originalAmount;
  const d = startOfDay(day);
  for (const reduction of reductions) {
    if (startOfDay(reduction.effectiveDate) <= d) {
      principal -= reduction.amount;
    }
  }
  return Math.max(0, principal);
};

const dailyInterestInRange = (originalAmount, reductions, from, to, rate) => {
  if (startOfDay(to) < startOfDay(from)) return 0;

  const factor = dailyFactor(rate);
  let interest = 0;
  let principal = remainingOnDate(originalAmount, reductions, from);
  let cursor = startOfDay(from);
  const end = startOfDay(to);

  const upcoming = reductions
    .filter((r) => {
      const when = startOfDay(r.effectiveDate);
      return when > cursor && when <= end;
    })
    .sort((a, b) => startOfDay(a.effectiveDate) - startOfDay(b.effectiveDate));

  for (const reduction of upcoming) {
    const changeOn = startOfDay(reduction.effectiveDate);
    const segEnd = startOfDay(addDays(changeOn, -1));
    if (cursor <= segEnd && principal > 0) {
      interest += principal * factor * countedInterestDays(cursor, segEnd);
    }
    principal = Math.max(0, principal - reduction.amount);
    cursor = changeOn;
  }

  if (cursor <= end && principal > 0) {
    interest += principal * factor * countedInterestDays(cursor, end);
  }

  return interest;
};

const interestForLoan = (loan, endDate, paidUpto, reductions, applyMinFloor) => {
  const start = startOfDay(loan.date);
  const amount = Number(loan.amount) || 0;
  const rate = Number(loan.interestRate);
  if (!amount || !rate) return 0;

  const end = startOfDay(endDate);
  if (end < start) return 0;

  const minEnd = startOfDay(addDays(start, MIN_INTEREST_DAYS - 1));
  const accrueFrom = paidUpto ? startOfDay(addDays(paidUpto, 1)) : start;

  let chargeEnd = end;
  if (applyMinFloor && end < minEnd) chargeEnd = minEnd;
  if (accrueFrom > chargeEnd) return 0;

  const factor = dailyFactor(rate);
  let interest = 0;

  const firstFrom = laterDate(accrueFrom, start);
  const firstTo = earlierDate(chargeEnd, minEnd);
  if (firstFrom <= firstTo) {
    interest += amount * factor * inclusiveDayCount(firstFrom, firstTo);
  }

  const dailyFrom = laterDate(accrueFrom, startOfDay(addDays(minEnd, 1)));
  const dailyTo = chargeEnd;
  if (dailyFrom <= dailyTo) {
    interest += dailyInterestInRange(amount, reductions, dailyFrom, dailyTo, rate);
  }

  return interest;
};

/**
 * Pending interest till endDate.
 * First 30 days from each loan always charge 1 month; after that, daily.
 * Principal paid reduces daily interest from the next day.
 */
export const calculatePendingInterest = (article, releaseDate, loanIndices = null, applyMinFloor = true) => {
  if (!article) return 0;

  const paidUpto = rebuildInterestPaidUpto(article);
  const endDate = releaseDate
    ? (parseLocalDate(releaseDate) || endOfDay(new Date(releaseDate)))
    : endOfDay(new Date());

  const allLoans = listArticleLoans(article);
  if (!allLoans.length) return 0;

  const repayments = (article.payments || [])
    .filter((p) => p.type === 'principal')
    .map((p) => ({ date: calendarStartOfDay(p.date) || startOfDay(p.date), amount: Number(p.amount) || 0 }));

  const byDate = allLoans
    .map((loan, index) => ({ loan, index }))
    .sort((a, b) => a.loan.date - b.loan.date || a.index - b.index);

  const reductionsByDateOrder = allocateRepaymentsFifo(
    byDate.map((item) => item.loan),
    repayments
  );
  const reductionsByIndex = allLoans.map(() => []);
  byDate.forEach((item, i) => {
    reductionsByIndex[item.index] = reductionsByDateOrder[i];
  });

  const selectedIndexes = loanIndices && Array.isArray(loanIndices) && article.loans?.length
    ? [...new Set(loanIndices)].filter((i) => allLoans[i])
    : allLoans.map((_, i) => i);

  let totalInterest = 0;
  for (const i of selectedIndexes) {
    totalInterest += interestForLoan(
      allLoans[i],
      endDate,
      paidUpto,
      reductionsByIndex[i],
      applyMinFloor !== false
    );
  }

  return Math.max(0, Math.round(totalInterest * 100) / 100);
};

/** Estimate interest for the next N unpaid 30-day blocks. */
export const estimateInterestForMonths = (article, months, loanIndices = null) => {
  const monthsNum = Number(months) || 0;
  if (!article || monthsNum <= 0) return 0;

  const paidUpto = rebuildInterestPaidUpto(article);
  const accrueFrom = paidUpto
    ? startOfDay(addDays(paidUpto, 1))
    : getLoanStartDate(article);
  const endDate = endOfDay(addDays(accrueFrom, monthsNum * MIN_INTEREST_DAYS - 1));

  return calculatePendingInterest(article, endDate, loanIndices, false);
};

/** Paid-upto after covering the next N 30-day blocks. */
export const suggestInterestPaidUptoDate = (article, months) => {
  const monthsNum = Number(months) || 0;
  if (!article || monthsNum <= 0) {
    return toDateInputValue(new Date());
  }

  const current = rebuildInterestPaidUpto(article);
  const advanced = advancePaidUptoByWholeMonths(article, current, monthsNum);
  return toDateInputValue(advanced || new Date());
};
